<template>
  <div>
    <input
      type="hidden"
      :name="fieldname"
      v-model="emailsContent">

    <div class="email-labels">
      <span v-for="(email, index) in emails" class="badge badge-success">
        {{ email }} <i class="fa fa-times" @click="removeEmail(index)"></i>
      </span>
    </div>

    <typeahead
      ref="typeahead"
      src="/webapi/user/search/email"
      :name="fieldname"
      :related-src="relatedSrc"
      :related-title="relatedTitle"
      :related-icon="relatedIcon"
      :placeholder="placeholder"
      @onUpdate="onUpdate"
      @onSelect="onSelect"
      @onSelectRelatedItem="onSelectRelatedItem"
      @onDirectInput="onDirectInput">
      <template #item="{ item }">
        <span v-if="allowManageLookupItem && item.is_lookup" class="delete-email-trigger">
          <a class="delete-email btn btn-xs btn-default btn-block"><i class="fa fa-trash"></i> Delete</a>
        </span>

        <span><i v-if="emails.indexOf(item.email) > -1" class="fa fa-check"> </i>{{item.email}}</span><br>
        <small v-if="item.name" v-text='item.name'></small>

        <small v-if="allowManageLookupItem && item.is_lookup" class="lookup-item-text">
          This email was added to the list manually. You may click "Delete" to permanently remove it from CIMS.
        </small>
      </template>
    </typeahead>
  </div>
</template>

<script>
export default {

  emits: ['update:modelValue', 'update:input'],

  props: {
    // Resulting input name attribute.
    fieldname: {
      type: String,
      default: ""
    },

    // Value.
    modelValue: {
      type: String,
      default: ""
    },

    // Typeahead placeholder text.
    placeholder: {
      type: String,
      default: "Specify email address and press Enter to add it to list..."
    },

    // Allow direct input of email address (without adding to list by pressing Enter)
    allowDirectInput: {
      type: Boolean,
      default: false
    },

    // Allow user to manage lookup email list items.
    allowManageLookupItem: {
      type: Boolean,
      default: false
    },

    // The source url to get first related suggestions from.
    relatedSrc: {
      type: String,
      default: '',
    },

    // The title for the related items list.
    relatedTitle: {
      type: String,
      default: '',
    },

    // The icon for the related items list.
    relatedIcon: {
      type: String,
      default: '',
    },
  },

  data () {
    return {
      // List of selected emails
      emails: [],

      // Target field content
      emailsContent: ''
    }
  },

  methods: {
    // The callback function which is triggered when the user hits on an item.
    onSelect (data) {
      // First process "Delete lookup item" button.
      if (data.event.target.classList.contains('delete-email')) {
        if (this.allowManageLookupItem) {
          this.confirmDeleteLookupItem(data.item.email);
        }
        return;
      }
      // Process adding the email to list.
      this.addEmail(data.item.email);
      this.$refs.typeahead.focus();
    },

    // The callback function which is triggered when the user hits on the related list item.
    onSelectRelatedItem(data) {
      const index = this.emails.indexOf(data.query);
      if (index === -1)
        this.addEmail(data.query);
      else
        this.removeEmail(index);
    },

    onDirectInput (data) {
      if (this.allowDirectInput) {
        this.addEmail(data.query);
      }
    },

    onUpdate(data) {
      this.$emit('update:input', data.query);
    },

    addEmail(email) {
      if (this.emails.indexOf(email) === -1) {
        // Allow space, comma and semicolon separation
        const emails = email.split(/[ ,;]+/).filter(e => e.trim() !== '');
        this.emails.push(...emails);

        this.refreshEmails();
      }
    },

    removeEmail(index) {
      this.emails.splice(index, 1);
      this.refreshEmails();
    },

    removeLastEmail() {
      if(this.isEmpty) {
        if(this.emails.length) {
          this.emails.pop();
          this.refreshEmails();
        }
      }
    },

    refreshEmails(directInput = '') {
      this.emailsContent = this.emails.join(",");
      if (directInput) {
        this.emailsContent = directInput + (this.emails.length ? ',' + this.emailsContent : '');
      }
    },

    /**
     * Delete lookup email list item confirmation dialog.
     */
    confirmDeleteLookupItem(email) {
      this.$swal.fire({
        title: 'Delete email',
        text: 'You are about to delete "' + email + '" from email lookup list. This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete'
      }).then((result) => {
        if (result.value) {
          this.deleteLookupItem(email);
        }
      })
    },

    /**
     * Delete lookup email list item.
     */
    deleteLookupItem(email) {
      const formData = new FormData();
      formData.append('_method', "DELETE");
      formData.append('email', email);
      axios.post('/webapi/lookup/email/delete', formData)
          .then(response => {
            this.emails = this.emails.filter(item => item !== email);
            this.$swal.fire(
                'Deleted',
                'The email "' + email + '" has been deleted.',
                'success'
            );
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to delete email')
          });
    },
  },
  mounted: function () {
    if(this.modelValue) {
      let valueEmails = this.modelValue.split(',');
      valueEmails.forEach(valueEmail => this.addEmail(valueEmail));
    }
  },
  watch: {
    emailsContent() {
      this.$emit('update:modelValue', this.emailsContent);
    },
  }
}
</script>

<style scoped>
.email-labels {
  margin-bottom: 0;
}
.email-labels span{
  margin: 3px;
}
.lookup-item-text {
  color: #b9b5b8;
}
span.delete-email-trigger {
  text-align: right;
  float: right
}
.active > span.delete-email-trigger {
  color: #2c3e50;
}
</style>
